import { useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_PREFIX = 'sergeanttap_resume_translation_feedback_seen';

/** Per-user storage key. Falls back to a global key only when user_id is
 *  not yet available — keeps the one-time guarantee robust without
 *  introducing any new backend dependency. */
export function feedbackSeenKey(userId) {
  return userId ? `${STORAGE_PREFIX}_${userId}` : STORAGE_PREFIX;
}

export function hasSeenResumeTranslationFeedback(userId) {
  try {
    return window.localStorage.getItem(feedbackSeenKey(userId)) === 'true';
  } catch {
    return false;
  }
}

export function markResumeTranslationFeedbackSeen(userId) {
  try {
    window.localStorage.setItem(feedbackSeenKey(userId), 'true');
  } catch {
    /* fail-soft */
  }
}

const SOURCE_CONTEXT = { trigger: 'post_resume_generation' };

async function postFeedback(payload) {
  await axios.post(
    `${BACKEND_URL}/api/feedback/report`,
    {
      type: 'resume_translation_feedback',
      blocked: false,
      page_path: window.location.pathname,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      source_context: SOURCE_CONTEXT,
      ...payload,
    },
    { withCredentials: true }
  );
}

/**
 * ResumeTranslationFeedbackPrompt — inline, non-blocking thumbs prompt
 * shown once per user after their first successful resume generation.
 *
 * Parent renders this with `open=true` exactly when:
 *   - first successful generation just happened AND
 *   - `hasSeenResumeTranslationFeedback(user_id)` is false
 * Parent passes `onDismiss` to flip its own state + call
 * `markResumeTranslationFeedbackSeen(user_id)`.
 *
 * Submits never block the user. Failures show a friendly toast and the
 * prompt closes (we still mark seen — we don't nag).
 */
function ResumeTranslationFeedbackPrompt({ open, onDismiss }) {
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpText, setFollowUpText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const close = useCallback(() => {
    setShowFollowUp(false);
    setFollowUpText('');
    setSubmitting(false);
    if (onDismiss) onDismiss();
  }, [onDismiss]);

  const sendPositive = useCallback(async () => {
    setSubmitting(true);
    try {
      await postFeedback({ rating: 'positive', message: '' });
      toast.success("Thanks. We'll review this personally.");
    } catch {
      toast.error("We couldn't send that just now. Please try again.");
    } finally {
      close();
    }
  }, [close]);

  const sendNegative = useCallback(async () => {
    setSubmitting(true);
    try {
      await postFeedback({
        rating: 'negative',
        message: (followUpText || '').trim(),
      });
      toast.success("Thanks. We'll review this personally.");
    } catch {
      toast.error("We couldn't send that just now. Please try again.");
    } finally {
      close();
    }
  }, [followUpText, close]);

  if (!open) return null;

  return (
    <>
      {/* Inline, non-blocking thumbs prompt. Lives above the Resume Preview
          paper canvas; never interrupts download/print/edit/regenerate. */}
      {!showFollowUp && (
        <div
          className="mx-4 lg:mx-8 mt-4 mb-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm print:hidden flex items-center justify-between gap-3"
          data-testid="resume-feedback-prompt"
        >
          <p className="text-sm text-slate-700">
            How well did Sergeant TAP translate your military experience?
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={sendPositive}
              disabled={submitting}
              data-testid="resume-feedback-thumbs-up"
            >
              <span className="mr-1">👍</span> Looks good
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowFollowUp(true)}
              disabled={submitting}
              data-testid="resume-feedback-thumbs-down"
            >
              <span className="mr-1">👎</span> Needs work
            </Button>
            <button
              type="button"
              onClick={close}
              disabled={submitting}
              aria-label="Dismiss feedback prompt"
              className="text-slate-400 hover:text-slate-600 p-1 rounded transition-colors"
              data-testid="resume-feedback-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Dialog
        open={showFollowUp}
        onOpenChange={(v) => { if (!submitting) setShowFollowUp(v); }}
      >
        <DialogContent className="max-w-md" data-testid="resume-feedback-followup-dialog">
          <DialogHeader>
            <DialogTitle>Tell us what to fix</DialogTitle>
          </DialogHeader>

          <div>
            <label
              htmlFor="resume-feedback-followup-textarea"
              className="block text-xs font-medium text-slate-600 mb-1"
            >
              What needs work?
            </label>
            <Textarea
              id="resume-feedback-followup-textarea"
              value={followUpText}
              onChange={(e) => setFollowUpText(e.target.value)}
              placeholder="Which part felt off? (e.g. summary tone, missing skills, role bullets)"
              rows={5}
              disabled={submitting}
              data-testid="resume-feedback-followup-textarea"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={close}
              disabled={submitting}
              data-testid="resume-feedback-followup-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={sendNegative}
              disabled={submitting}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="resume-feedback-followup-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ResumeTranslationFeedbackPrompt;
